/**
 * Marketplace's own vocabulary, mapped onto the panel-wide design tokens in
 * component/common/designSystem/theme.js.
 *
 * Only domain meaning lives here — palettes, chart defaults and the chip
 * classes themselves belong to the design system.
 */
import {
  CHART_COLORS,
  toneChip,
  toneColor,
  toneColors,
  toneLookup
} from '../../component/common/designSystem';

// Order lifecycle: a verdict rather than a scale, so each bucket takes its
// semantic tone. Order matches the backend's Success → Processing sequence.
export const ORDER_STATUS_COLORS = toneColors([
  'success',
  'danger',
  'warning',
  'info',
  'brand'
]);

const orderStatusTone = toneLookup({
  success: 'success',
  failed: 'danger',
  pending: 'warning',
  requested: 'info',
  processing: 'brand'
});

export const orderStatusColor = (label) => toneColor(orderStatusTone(label));
export const orderStatusChipClass = (label) => toneChip(orderStatusTone(label));

/** Bought for self vs sent as a gift. */
export const ORDER_TYPE_COLORS = toneColors(['brand', 'accent']);

/** VoucherLine / BHN / Direct — fulfilment routes, no inherent order. */
export const PROVIDER_COLORS = toneColors(['brand', 'teal', 'neutral']);

/** Offer / Coupon code / Store link. */
export const CLICK_TYPE_COLORS = toneColors(['neutral', 'accent', 'brand']);

/**
 * Ordered ramps for the distribution charts. Each is a sequence rather than a
 * set of categories, so they get a ramp instead of the categorical palette —
 * and each has exactly as many colours as its band list has bands, so a slice
 * never borrows its neighbour's meaning.
 */

// Face value, Under £10 → £100+. Bigger is not better here, just bigger, so
// this runs cool → warm rather than bad → good.
export const VALUE_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.orange
];

// Orders placed per person: 1 → 8+.
export const ORDERS_PER_USER_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

// Advertised discount, Under 10% → 50%+. A deeper discount is a better deal,
// so this is a plain shallow → deep ramp.
export const DISCOUNT_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

/**
 * Runway before an offer lapses: Expired → Over 3 months. Red at the expired
 * end because that band is the one that needs acting on, not celebrating.
 */
export const EXPIRY_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.greenDeep
];
